import { Component } from '@theme/component';

/**
 * @typedef {object} KatuaProductTabsRefs
 * @property {HTMLElement} tablist - Container the tab buttons are built into.
 */

/**
 * Katua product information tabs. Builds one tab button per `.katua-tab` panel (rendered by the
 * `_katua-product-tab` child blocks) using its `data-tab-title`, and follows the ARIA tabs pattern:
 * arrow keys, Home and End move between tabs. In the theme editor, selecting a tab block shows it.
 *
 * @extends Component<KatuaProductTabsRefs>
 */
class KatuaProductTabs extends Component {
  requiredRefs = ['tablist'];

  /** @type {AbortController} */
  #listeners = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#build();

    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    this.refs.tablist.addEventListener('click', this.#handleClick, { signal });
    this.refs.tablist.addEventListener('keydown', this.#handleKeydown, { signal });
    document.addEventListener('shopify:block:select', this.#handleEditorSelect, { signal });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#build();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners.abort();
  }

  /**
   * @returns {HTMLElement[]}
   */
  get #panels() {
    return Array.from(this.querySelectorAll('.katua-tab')).filter((panel) => panel instanceof HTMLElement);
  }

  /**
   * @returns {HTMLButtonElement[]}
   */
  get #tabs() {
    return Array.from(this.refs.tablist.querySelectorAll('[role="tab"]')).filter(
      (tab) => tab instanceof HTMLButtonElement
    );
  }

  /**
   * (Re)builds the tab buttons from the panels, keeping the selected tab when possible.
   */
  #build() {
    const panels = this.#panels;
    const previouslySelected = this.#tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    const selectedId = previouslySelected?.getAttribute('aria-controls');

    const tabs = panels.map((panel, index) => {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'katua-tabs__tab';
      tab.id = `${panel.id}-tab`;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panel.id);
      tab.textContent = panel.dataset.tabTitle || String(index + 1);

      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
      panel.tabIndex = 0;
      return tab;
    });

    this.refs.tablist.replaceChildren(...tabs);

    const initial = panels.findIndex((panel) => panel.id === selectedId);
    this.#select(initial === -1 ? 0 : initial, false);
  }

  /**
   * @param {number} index
   * @param {boolean} focus
   */
  #select(index, focus) {
    const tabs = this.#tabs;
    const panels = this.#panels;
    if (!tabs.length) return;

    const target = (index + tabs.length) % tabs.length;

    tabs.forEach((tab, i) => {
      const selected = i === target;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      const panel = panels[i];
      if (panel) panel.hidden = !selected;
    });

    if (focus) tabs[target]?.focus();
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const tab = event.target.closest('[role="tab"]');
    if (!(tab instanceof HTMLButtonElement)) return;

    this.#select(this.#tabs.indexOf(tab), false);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const tabs = this.#tabs;
    const current = tabs.findIndex((tab) => tab === document.activeElement);
    if (current === -1) return;

    const isRtl = getComputedStyle(this).direction === 'rtl';
    const next = isRtl ? 'ArrowLeft' : 'ArrowRight';
    const previous = isRtl ? 'ArrowRight' : 'ArrowLeft';

    /** @type {number | undefined} */
    let target;
    if (event.key === next) target = current + 1;
    else if (event.key === previous) target = current - 1;
    else if (event.key === 'Home') target = 0;
    else if (event.key === 'End') target = tabs.length - 1;
    if (target === undefined) return;

    event.preventDefault();
    this.#select(target, true);
  };

  /**
   * Shows the tab whose block was selected in the theme editor.
   * @param {Event} event
   */
  #handleEditorSelect = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;
    const panel = event.target.closest('.katua-tab');
    if (!(panel instanceof HTMLElement)) return;

    this.#select(this.#panels.indexOf(panel), false);
  };
}

if (!customElements.get('katua-product-tabs')) {
  customElements.define('katua-product-tabs', KatuaProductTabs);
}
